import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";

export const InstallPwaModal = forwardRef(({ darkMode = false }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [appInfo, setAppInfo] = useState({
    name: "Salesive App",
    icon: null,
  });

  useEffect(() => {
    const fetchManifest = async () => {
      try {
        const link = document.querySelector('link[rel="manifest"]');
        const manifestUrl = link ? link.href : "/manifest.json";
        const response = await fetch(manifestUrl);
        if (response.ok) {
          const manifest = await response.json();
          const name = manifest.name || manifest.short_name || "App";

          // Find a suitable icon (prefer png, 192x192 or larger)
          let iconUrl = null;
          if (manifest.icons && manifest.icons.length > 0) {
            const icon =
              manifest.icons.find((i) => i.sizes?.includes("192x192")) ||
              manifest.icons[0];
            iconUrl = icon.src;
            // Handle relative paths
            if (
              iconUrl &&
              !iconUrl.startsWith("http") &&
              !iconUrl.startsWith("/")
            ) {
              const manifestBase = manifestUrl.substring(
                0,
                manifestUrl.lastIndexOf("/") + 1,
              );
              iconUrl = manifestBase + iconUrl;
            }
          }

          setAppInfo({
            name,
            icon: iconUrl,
          });
        }
      } catch (error) {
        console.warn("[SalesiveWidget] Failed to fetch manifest:", error);
      }
    };

    fetchManifest();
  }, []);

  useImperativeHandle(ref, () => ({
    openModal: () => {
      if (deferredPrompt || isIOS) {
        setIsVisible(true);
      } else {
        console.warn("[SalesiveWidget] No installation prompt available");
      }
    },
    isInstallable: () => !!deferredPrompt || isIOS,
  }));

  useEffect(() => {
    // Detect iOS
    const isIosDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone =
      window.navigator.standalone ||
      window.matchMedia("(display-mode: standalone)").matches;

    if (isIosDevice && !isStandalone) {
      setIsIOS(true);
      checkInteractionAndSchedule();
    }

    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);

      // Check if we should show the modal
      checkInteractionAndSchedule();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  const checkInteractionAndSchedule = () => {
    // Check for permanent acceptance (though browser handles this mostly)
    const alreadyAccepted =
      localStorage.getItem("salesive_pwa_interaction") === "accepted";
    if (alreadyAccepted || hasInteracted) return;

    // Check if declined recently
    const declinedAt = localStorage.getItem("salesive_pwa_declined_at");
    if (declinedAt) {
      const twoDays = 2 * 24 * 60 * 60 * 1000;
      const timeSinceDecline = Date.now() - Number(declinedAt);
      if (timeSinceDecline < twoDays) {
        return;
      }
    }

    // Show after a delay to not be intrusive immediately
    setTimeout(() => {
      setIsVisible(true);
    }, 30000); // 30 seconds
  };

  const handleInstall = async () => {
    if (isIOS) {
      // For iOS, the user just needs to follow instructions, so we close the modal
      // We count this as an "interaction" but maybe not a full "acceptance" tracked by browser
      // But for our modal logic, we can say they saw it.
      setIsVisible(false);
      setHasInteracted(true);
      return;
    }

    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("[SalesiveWidget] User accepted the install prompt");
      localStorage.setItem("salesive_pwa_interaction", "accepted");
      localStorage.removeItem("salesive_pwa_declined_at");
      setIsVisible(false);
      setHasInteracted(true);
    } else {
      console.log("[SalesiveWidget] User dismissed the install prompt");
      // Treat dismissal as a decline for now
      localStorage.setItem("salesive_pwa_declined_at", Date.now().toString());
    }

    // We've used the prompt, and can't use it again, discard it
    setDeferredPrompt(null);
  };

  const handleDecline = () => {
    setIsVisible(false);
    setHasInteracted(true);
    // Don't ask again for 2 days
    localStorage.setItem("salesive_pwa_declined_at", Date.now().toString());
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        when: "afterChildren",
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.5 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 25 },
    },
    exit: { opacity: 0, scale: 0.9, y: 20 },
  };

  // Don't render component at all if no prompt stored (unless forced visible somehow, but logic prevents it)
  if (!deferredPrompt && !isIOS && !isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="tw-fixed tw-inset-0 tw-z-[2147483648] tw-flex tw-items-center tw-justify-center tw-pointer-events-none"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
        >
          {/* Backdrop */}
          <motion.div
            className="tw-absolute tw-inset-0 tw-bg-black tw-pointer-events-auto"
            onClick={handleDecline}
            variants={backdropVariants}
          />

          {/* Modal */}
          <motion.div
            className={`
                            tw-relative tw-w-[85%] tw-max-w-sm tw-p-5 tw-rounded-2xl tw-shadow-xl tw-pointer-events-auto
                            ${
                              darkMode
                                ? "tw-bg-gray-800 tw-text-white"
                                : "tw-bg-white tw-text-gray-900"
                            }
                        `}
            variants={modalVariants}
          >
            {/* App Icon & Name */}
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
                  <div className="tw-bg-transparent tw-w-14 tw-h-14 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shadow-md tw-flex-shrink-0 tw-overflow-hidden">
                {appInfo.icon ? (
                  <img
                    src={appInfo.icon}
                    alt={appInfo.name}
                    className="tw-w-full tw-h-full tw-object-cover"
                  />
                ) : (
                  <div className="tw-bg-primary tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="tw-w-8 tw-h-8 tw-text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="tw-flex-1">
                <h3 className="tw-text-base tw-font-semibold tw-mb-0.5">
                  {appInfo.name}
                </h3>
                <p
                  className={`tw-text-xs ${
                    darkMode ? "tw-text-gray-400" : "tw-text-gray-500"
                  }`}
                >
                  Add to Home Screen
                </p>
              </div>
            </div>

            {/* iOS Instructions */}
            {isIOS && (
              <div
                className={`tw-text-sm tw-mb-4 tw-p-3 tw-rounded-lg ${
                  darkMode ? "tw-bg-gray-700/50" : "tw-bg-gray-50"
                }`}
              >
                <p
                  className={darkMode ? "tw-text-gray-300" : "tw-text-gray-600"}
                >
                  Tap{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="tw-inline tw-w-4 tw-h-4 tw-text-primary tw-mx-0.5 tw-align-text-bottom"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>{" "}
                  then <strong>"Add to Home Screen"</strong>
                </p>
              </div>
            )}

            {/* Benefits - Non-iOS only */}
            {!isIOS && (
              <p
                className={`tw-text-sm tw-mb-4 ${
                  darkMode ? "tw-text-gray-400" : "tw-text-gray-500"
                }`}
              >
                Quick access from your home screen. No download required.
              </p>
            )}

            {/* Buttons */}
            <div className="tw-flex tw-gap-2">
              <span
                onClick={handleDecline}
                className={`
                                    tw-flex-1 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors tw-text-center tw-cursor-pointer
                                    ${
                                      darkMode
                                        ? "tw-text-gray-400 hover:tw-bg-gray-700"
                                        : "tw-text-gray-500 hover:tw-bg-gray-100"
                                    }
                                `}
              >
                Not now
              </span>
              <span
                onClick={handleInstall}
                className="tw-flex-1 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium tw-bg-primary tw-text-white hover:tw-bg-primary/90 tw-transition-colors tw-text-center tw-cursor-pointer"
              >
                {isIOS ? "Done" : "Install"}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
